import {Tooltip} from '@mui/material';
import React from 'react';

import {initials, nameFromEmail} from '../lib/aggregate';

interface Props {
    email: string;
    /** Display name. Defaults to a humanised version of `email`. */
    name?: string;
    /** When provided, renders a trailing X button and wires it. */
    onRemove?: () => void;
    /** When provided, makes the badge clickable (separate from the X). */
    onClick?: (email: string) => void;
}

/**
 * Standard "this is a user" badge — avatar + name in a small pill. Shares the
 * same `.ac.ac-user` DOM and CSS as UserChip on Grants pages, but uses the
 * `k-identity` (green) variant since there's no access-kind context here.
 * Use this wherever you just need to display a user reference (recipient
 * picker, eventually request lists, etc.).
 */
export function UserBadge({email, name, onRemove, onClick}: Props) {
    const displayName = (name ?? nameFromEmail(email)) || email;
    const className = ['ac', 'ac-user', 'k-identity', onClick ? '' : 'static']
        .filter(Boolean)
        .join(' ');
    const interactive = onClick
        ? {
              role: 'button' as const,
              tabIndex: 0,
              onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onClick(email);
              },
              onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      onClick(email);
                  }
              },
          }
        : {};
    return (
        <Tooltip title={`${displayName} · ${email}`} placement="top" arrow>
            <span className={className} {...interactive}>
                <span className="avatar avatar-chip" aria-hidden>
                    {initials(displayName, email)}
                </span>
                <span className="name">{displayName}</span>
                {onRemove && (
                    <button
                        type="button"
                        className="ac-remove"
                        aria-label={`Remove ${displayName}`}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => {
                            e.stopPropagation();
                            onRemove();
                        }}
                    >
                        ×
                    </button>
                )}
            </span>
        </Tooltip>
    );
}
